<template>
  <div
    class="gmb-attributes-mass-edit"
    :class="{ disabled: !canEditFacilityServices }"
  >
    <v-row class="max-w-560">
      <v-col class="d-flex align-center pb-0 max-w-50px">
        <v-checkbox
          class="mt-0"
          v-model="checkbox"
          @change="onCheckboxClicked($event)"
          :disabled="!canEditFacilityServices"
        ></v-checkbox>
      </v-col>
      <v-col class="d-flex align-center pb-0">
        <paragraph class="border-accent">
          {{ $t("message.facilityDetails.services.title") }}
        </paragraph>
      </v-col>
    </v-row>
    <v-row v-if="!services.length">
      <v-col class="max-w-50px" />
      <v-col>
        <div class="description">
          {{ $t("message.facilityDetails.services.noServicesSelected") }}
        </div>
        <InfoBox
          class="mt-2"
          :text="$t('message.facilityDetails.services.addServicesInfo')"
        />
        <div class="actions" v-if="checkbox">
          <div class="action" v-for="action in actions" :key="action.type">
            <div class="description muted">
              {{ $t(action.msg) }}
            </div>
            <v-btn v-bind="action.btnProps" @click="handleAction(action)">
              {{ $t(action.btnLabel) }}
            </v-btn>
            <v-tooltip right :max-width="tooltipMaxWidth">
              <template v-slot:activator="{ on }">
                <div class="info-icon" v-on="on">
                  <v-icon color="lime">mdi-information</v-icon>
                </div>
              </template>
              {{ $t(action.desc) }}
            </v-tooltip>
          </div>
        </div>
      </v-col>
    </v-row>
    <template v-if="services.length">
      <v-row>
        <v-col class="max-w-50px" />
        <v-col>
          <div class="active-action">
            <v-row>
              <v-col cols="11" class="d-flex align-center">
                <span class="section-title">{{ activeActionLabel }}:</span>
              </v-col>
              <v-col cols="1">
                <v-btn
                  icon
                  color="primary"
                  @click="handleAction(selectedAction, services)"
                >
                  <v-icon>
                    icon-edit
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="max-w-50px" />
        <v-col>
          <div>
            <GmbServicesMassPreview
              v-if="
                services.filter(item => !('gmb_service_type' in item)).length
              "
              :title="$t('message.facilityDetails.services.customServices')"
              :services="services.filter(item => !('gmb_service_type' in item))"
              :delete-mode="selectedActionType === actionTypes.DELETE"
            />
          </div>
          <GmbServicesPreview
            :services="selectedServicesWithCategories"
            :delete-mode="selectedActionType === actionTypes.DELETE"
          />

          <div class="d-flex align-center mt-4">
            <div class="description muted mb-0">
              {{ $t("message.facilityDetails.attributes.changeType") }}
            </div>
            <v-btn
              v-for="action in notActiveActions"
              :key="action.type"
              text
              color="primary"
              @click="changeAction(action)"
              class="ml-4"
            >
              {{ $t(action.btnLabel) }}
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </template>
    <DialogGmbServicesTree
      v-if="dialog"
      is-open
      :preselected-services-list="selectedServicesGMB"
      :preselected-custom-services="selectedCustomServices"
      :facilities-ids="facilitiesIds"
      :title="selectedAction.btnLabel"
      :message="selectedAction.desc"
      :delete-mode="selectedAction.type === actionTypes.DELETE"
      @save="handleSave"
      @close="dialog = false"
    />
  </div>
</template>

<script>
import Paragraph from "@/components/ui/Paragraph";
import DialogGmbServicesTree from "@/components/FacilityDetails/GmbServices/GmbServicesTree";
import GmbServicesPreview from "@/components/FacilityDetails/GmbServices/GmbServicesPreview.vue";
import GmbServicesMassPreview from "@/components/FacilityDetails/GmbServices/GmbServicesMassPreview.vue";
import InfoBox from "@/components/ui/InfoBox.vue";
import { DialogHelper } from "@/helpers/dialog-helper";
import { GmbActionType } from "@/enums/gmb-attribute-action-type";
import { permissionsInfo } from "@/helpers/mixins/permissions-info";

export default {
  name: "GmbServicesMassEdit",
  components: {
    Paragraph,
    DialogGmbServicesTree,
    GmbServicesPreview,
    GmbServicesMassPreview,
    InfoBox
  },
  mixins: [permissionsInfo],
  data() {
    return {
      dialog: false,
      checkbox: false,
      services: [],
      selectedServicesWithCategories: [],
      selectedServices: [],
      tooltipMaxWidth: 436,
      actions: [],
      facilitiesIds: [],
      actionTypes: GmbActionType,
      selectedAction: null,
      selectedActionType: null
    };
  },
  computed: {
    activeActionLabel() {
      const labelKey = "message.facilityDetails.services.";
      if (this.selectedActionType === this.actionTypes.ADD) {
        return this.$t(labelKey + "addServices");
      } else if (this.selectedActionType === this.actionTypes.DELETE) {
        return this.$t(labelKey + "deleteServices");
      } else if (this.selectedActionType === this.actionTypes.SET) {
        return this.$t(labelKey + "overwriteServices");
      } else {
        return "";
      }
    },
    selectedServicesGMB() {
      return this.selectedServices.filter(item => item.gmb_service_type);
    },

    selectedServiceItems() {
      const selectedServices = this.selectedServices.filter(
        item => item.gmb_service_type
      );

      return selectedServices.map(({ gmb_service_type, description }) => {
        return { ...gmb_service_type, description };
      });
    },
    selectedCustomServices() {
      return this.selectedServices.filter(item => !item.gmb_service_type);
    },
    notActiveActions() {
      return this.actions.filter(
        ({ type }) => type !== this.selectedActionType
      );
    }
  },
  mounted() {
    this.facilitiesIds = this.$route.params.ids.split(",");
  },
  created() {
    this.setActions();
  },
  methods: {
    setActions() {
      this.actions = [
        {
          type: this.actionTypes.ADD,
          msg: "message.facilityDetails.services.addServicesQuestion",
          desc: "message.facilityDetails.services.addServicesDescription",
          btnLabel: "message.facilityDetails.services.addServices",
          btnProps: {
            color: "primary"
          }
        },
        {
          type: this.actionTypes.DELETE,
          msg: "message.facilityDetails.services.deleteServicesQuestion",
          desc: "message.facilityDetails.services.deleteServicesDescription",
          btnLabel: "message.facilityDetails.services.deleteServices",
          btnProps: {
            color: "primary"
          }
        },
        {
          type: this.actionTypes.SET,
          msg: "message.facilityDetails.services.overwriteServicesQuestion",
          desc: "message.facilityDetails.services.overwriteServicesDescription",
          btnLabel: "message.facilityDetails.services.overwriteServices",
          btnProps: {
            color: "primary",
            outlined: true
          }
        }
      ];
    },
    onCheckboxClicked(val) {
      this.$emit("update:checked", val);
      if (!val) {
        this.services = [];
        this.selectedAction = null;
        this.selectedActionType = null;
        this.$emit("update:service", this.services);
      }
    },
    changeAction(type) {
      DialogHelper.showConfirmDialog(
        {
          text: this.$t("message.facilityDetails.services.changeTypeConfirm"),
          okLabelKey: "message.common.continue"
        },
        () => {
          this.handleAction(type);
        }
      );
    },
    handleAction(action, services = []) {
      this.selectedAction = action;
      this.$set(this, "selectedServices", [...services]);
      this.dialog = true;
    },
    handleSave({ payload, selectedServicesWithCategories }) {
      this.services = payload.service_items.map(service =>
        Object.assign({}, service)
      );

      this.selectedServicesWithCategories = selectedServicesWithCategories;
      this.selectedActionType = this.selectedAction.type;

      this.$emit("update:type", this.selectedActionType);
      this.$emit("update:service", this.services);
    }
  }
};
</script>

<style lang="scss" scoped>
.gmb-attributes-mass-edit {
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  padding-top: 12px;
  padding-bottom: 30px;
}

.section-title {
  @include font-size(16px);
  line-height: 24px;
  font-weight: 600;
  color: $grey-darker;
}

.description {
  @include font-size(14px);
  line-height: 17px;
  color: $grey-darker;
  margin-bottom: $spacing;

  &.muted {
    line-height: 19px;
    opacity: 0.7;
  }
}

.actions {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-right: -$spacing * 5.75;

  .action {
    flex-basis: 170px;
    position: relative;
    margin-top: $spacing * 5;
    margin-right: $spacing * 5.75;

    .description {
      text-align: center;
      margin-bottom: $spacing * 2.5;
    }

    .info-icon {
      display: inline;
      position: absolute;
      top: 6px;
      right: -30px;
    }
  }

  .v-btn {
    width: 100%;
  }
}

.active-action {
  border-bottom: 1px solid $light-3;
}

.max-w-560 {
  max-width: 560px;
}

.border-accent {
  @include borderAccent($lime);
}

.v-btn {
  text-transform: none;
}
</style>
